import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

export const Dropdown = ({
  trigger,
  children,
  items,
  align = 'right',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, right: 0 });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        left: rect.left,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isOpen]);

  const alignmentStyles = {
    left: { left: position.left },
    right: { right: position.right },
    center: { left: position.left + (triggerRef.current?.offsetWidth || 0) / 2, transform: 'translateX(-50%)' },
  };

  const dropdownContent = isOpen && (
    <div
      ref={dropdownRef}
      className={`
        fixed z-[100] min-w-[200px]
        bg-white rounded-lg shadow-lg border border-gray-200
        py-1 animate-fade-in
        ${className}
      `}
      style={{
        top: `${position.top}px`,
        ...alignmentStyles[align],
      }}
    >
      {items ? (
        items.map((item, index) => (
          <button
            key={index}
            onClick={() => {
              item.onClick?.();
              setIsOpen(false);
            }}
            className="w-full flex items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
            <span>{item.label}</span>
          </button>
        ))
      ) : (
        children
      )}
    </div>
  );

  return (
    <>
      <div className="relative inline-block">
        <div ref={triggerRef} onClick={() => setIsOpen(!isOpen)}>
          {trigger}
        </div>
      </div>
      
      {isOpen && createPortal(dropdownContent, document.body)}
    </>
  );
};

export const DropdownItem = ({
  children,
  onClick,
  icon,
  danger = false,
  className = '',
}) => {
  return (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center gap-3 px-4 py-2
        text-left text-sm
        ${danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'}
        transition-colors
        ${className}
      `}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span>{children}</span>
    </button>
  );
};

export const DropdownDivider = () => {
  return <div className="my-1 border-t border-gray-200" />;
};
